/**
 * useNavItems — resolves the navigation configuration for the current user.
 * Returns sectioned nav items with proper state (active/locked/hidden),
 * role badge, banners, and company info.
 */
import { useMemo } from 'react';
import { useUserContext } from '../contexts/UserContext';
import { useAppContext } from '../contexts/AppContext';
import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import { hasPermission, Permission } from '../utils/permissions';
import {
    ALL_NAV_ITEMS,
    NavRole,
    SubscriptionState,
    NavState,
    NavSection,
    ResolvedNavItem,
    NavBanner,
    NavItemState,
    getSectionLabel,
    getRoleBadge,
} from '../config/navConfig';

export function useNavItems(): NavState {
    const { company, companyUser, companyOwner, businessAdmin } = useUserContext();
    const { issueCount, inboxCount } = useAppContext();
    const { accountStatus, trialDaysRemaining, isModuleLocked, isTrialActive } = useSubscriptionContext();

    return useMemo(() => {
        // Determine role
        const role: NavRole = businessAdmin
            ? 'businessAdmin'
            : (companyOwner && !companyUser)
                ? 'companyAdmin'
                : companyUser
                    ? (hasPermission(companyOwner, companyUser, Permission.MANAGE_USERS, businessAdmin) ? 'companyAdmin' : 'fieldStaff')
                    : 'companyAdmin'; // fallback

        // Determine subscription state — prefer new accountStatus, fall back to old subscriptionType
        const newAccountStatus = (company as Record<string, unknown>)?.accountStatus as string | undefined;
        const legacySubType = company?.subscriptionType || companyOwner?.subscriptionType || 'active';

        let subscriptionState: SubscriptionState = 'active';
        if (newAccountStatus) {
            // New model
            if (newAccountStatus === 'trial') subscriptionState = 'free-demo';
            else if (newAccountStatus === 'paused' || newAccountStatus === 'cancelled' || newAccountStatus === 'archived') subscriptionState = 'paused';
            else if (newAccountStatus === 'past-due') subscriptionState = 'active'; // still accessible during grace period
            else subscriptionState = 'active';
        } else {
            // Legacy fallback
            if (legacySubType === 'paused') subscriptionState = 'paused';
            else if (legacySubType === 'free-demo') subscriptionState = 'free-demo';
            else if (legacySubType === 'expired') subscriptionState = 'expired';
        }

        const isPaused = subscriptionState === 'paused' || subscriptionState === 'expired';
        const isFreeDemo = subscriptionState === 'free-demo';

        const companyName = company?.name || '';

        // Filter and resolve items
        const resolvedItems: { section: string; item: ResolvedNavItem }[] = [];

        for (const navItem of ALL_NAV_ITEMS) {
            // Role filter
            if (navItem.roles && !navItem.roles.includes(role)) continue;

            // Admin-only items hidden from field staff (they get the fieldStaff variant instead)
            if (navItem.adminOnly && role === 'fieldStaff') continue;

            // Determine text and href (field staff overrides)
            let text = navItem.text;
            let href = navItem.href;
            if (role === 'fieldStaff') {
                if (navItem.fieldStaffText) text = navItem.fieldStaffText;
                if (navItem.fieldStaffHref) href = navItem.fieldStaffHref;
            }

            // Determine item state
            let state: NavItemState = 'active';

            // If subscription is paused/expired, lock everything except system items
            if (isPaused && navItem.section !== 'system') {
                // Only "Get Help" stays active in support when paused
                if (navItem.section === 'support' && navItem.id === 'get-help') {
                    state = 'active';
                } else {
                    state = 'locked';
                }
            }
            // Module enrollment check — use SubscriptionContext if available, fall back to legacy
            else if (navItem.moduleEnrollment && !isFreeDemo) {
                // Map moduleEnrollment field to ModuleKey for SubscriptionContext check
                const moduleKeyMap: Record<string, string> = {
                    pmModuleEnrollment: 'pm',
                    tiresModuleEnrollment: 'tires',
                    trainingModuleEnrollment: 'training',
                    costTrackingModuleEnrollment: 'costTracking',
                    skillTrackingModuleEnrollment: 'skillTracking',
                };
                const moduleKey = moduleKeyMap[navItem.moduleEnrollment];

                if (moduleKey && newAccountStatus) {
                    // New subscription model — check via context
                    if (isModuleLocked(moduleKey as any)) {
                        state = 'locked';
                    }
                } else {
                    // Legacy fallback — check boolean on company
                    const enrolled = company?.[navItem.moduleEnrollment as keyof typeof company] === true;
                    if (!enrolled) {
                        state = 'locked';
                    }
                }
            }

            // Permission check — if user doesn't have permission, lock (not hide)
            const requiredPermissions = navItem.permissions ?? (navItem.permission ? [navItem.permission] : []);
            if (requiredPermissions.length > 0 && state === 'active') {
                const hasPerm = requiredPermissions.some((permission) =>
                    hasPermission(companyOwner, companyUser, permission, businessAdmin),
                );
                if (!hasPerm) {
                    state = 'locked';
                }
            }

            resolvedItems.push({
                section: navItem.section,
                item: {
                    id: navItem.id,
                    text,
                    href,
                    icon: navItem.icon,
                    state,
                    badgeKey: navItem.badgeKey,
                    isLogout: navItem.id === 'logout',
                },
            });
        }

        // Group into sections
        const sectionOrder = ['main', 'support', 'system'];
        const sections: NavSection[] = sectionOrder
            .map(sectionId => ({
                id: sectionId,
                label: getSectionLabel(sectionId, role, companyName),
                items: resolvedItems
                    .filter(r => r.section === sectionId)
                    .map(r => r.item),
            }))
            .filter(s => s.items.length > 0);

        // Build banner
        let banner: NavBanner | undefined;
        if (isPaused) {
            banner = {
                type: 'paused',
                message: 'Access has been suspended.\nReactivate to restore module access.',
                ctaText: 'Reactivate Subscription',
                ctaHref: '/manage/subscription',
            };
        } else if (isFreeDemo) {
            // Use new trialDaysRemaining if available, otherwise calculate from legacy field
            let daysRemaining = trialDaysRemaining ?? 30;
            if (trialDaysRemaining === null) {
                const expDate = company?.subscriptionExpiration;
                if (expDate) {
                    const diff = new Date(expDate).getTime() - Date.now();
                    daysRemaining = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
                }
            }
            banner = {
                type: 'free-demo',
                message: `${daysRemaining} days remaining — full access to all modules`,
                ctaText: 'Upgrade to Paid Plan',
                ctaHref: '/manage/subscription',
                daysRemaining,
            };
        }

        // Role badge
        const roleBadge = getRoleBadge(role);

        // User display name
        const userName = role === 'fieldStaff'
            ? `${companyUser?.name || ''} - STAFF`
            : role === 'businessAdmin'
                ? 'SUPER ADMIN ACCOUNT'
                : 'ADMIN ACCOUNT';

        return {
            role,
            roleBadge,
            subscriptionState,
            companyName,
            companyLogo: company?.logo || undefined,
            userName,
            sections,
            banner,
        };
    }, [company, companyUser, companyOwner, businessAdmin, issueCount, inboxCount, accountStatus, trialDaysRemaining, isModuleLocked, isTrialActive]);
}
